import {IWithRouterProps} from "@athena-portal/components";
import {
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    FEEDBACK_DIALOG_DISPLAY_COUNTER,
    FEEDBACK_DIALOG_DISPLAY_EVENT,
    ICacheManager,
    ICustomerPortalService,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTC,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_SUPPORT_PORTAL_MTC,
    MarketingPreference,
    Party,
    PartyProperty,
    PartyPropertyEnum,
    Policy,
    POLICY_NUMBER_PREFIX_MAP,
    POLICY_STATUS_MTC,
    productHelper,
    ProductsEnum,
    Quote,
    Renewal,
    SessionStorageItemEnum,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {
    getProductByPolicyNumber,
    getSessionStorage,
    hasPoliciesBeforeRebranding,
    isCancellationProcess,
    setSessionStorage
} from "../../../../CommonFunctions";
import {finalize} from "rxjs/operators";
import * as React from "react";


export interface IDashboardParams {
    policyNumber: string;
    [key: string]: string | undefined;
}

export interface IBaseDashboardState {
    policy?: Policy;
    hasExpiredPolicies?: boolean;
    hasOldPolicies?: boolean;
    party?: Party;
    partyLoaded: boolean;
    quotes?: Quote[];
    quotesLoaded: boolean;
    policiesProcessed: boolean;
    policyPropertiesLoaded: boolean;
    showRenewalSwitchConfirmation: boolean;
    instalmentsInfo?: InstalmentsInfo;
    allInstalmentsInfo?: InstalmentsInfo;
    missedPaymentInfo?: InstalmentsInfo;
    instalmentInfoLoaded: boolean;
    renewalProposal?: Renewal;
    automaticRenewal: boolean;
    performRenewal: boolean | undefined;
    showAutoRenewalQuestionModal: boolean;
    showAutoRenewalQuestionSuccessModal: boolean;
    reuseCard: boolean;
    doRedirectToAllianzPromotionPage: boolean;
    newQuotePopUpForProduct: string;
    marketingPreferencesEdited: boolean;
    marketingPreferenceCheckboxStates: Record<string, boolean>;
    marketingPreferencesSaving: boolean;
    showMarketingPreferencesInSuccessPopup: boolean,
    hasFailedCardRefundAndApplicableForDD: boolean;
    cardRefundStatusChecked: boolean;
    doRedirectToAllianzOnlinePage: boolean;
    displayOverlay: boolean;
}

export abstract class DashboardPagePlain<T extends IBaseDashboardState> extends React.Component<IWithRouterProps<IDashboardParams>, T> {
    protected showSuccessPopup: string | null; // NOSONAR
    protected referrerJourney: string | null;
    protected multiPolicyAccount: boolean; // NOSONAR
    protected hideAutoRenewalBanner: boolean; // NOSONAR
    protected product: ProductsEnum;
    protected pushToAdobeDataLayerTimeout: any; // NOSONAR

    @lazyInject(Types.CustomerPortalService)
    protected customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    protected cacheManager: ICacheManager;

    constructor(props: IWithRouterProps<IDashboardParams>) {
        super(props);

        this.state = {
            quotesLoaded: false,
            policiesProcessed: false,
            partyLoaded: false,
            instalmentInfoLoaded: false,
            policyPropertiesLoaded: false,
            displayOverlay: false,
            instalmentsInfo: {} as InstalmentsInfo,
            allInstalmentsInfo: {} as InstalmentsInfo,
            automaticRenewal: false,
            performRenewal: false,
            showAutoRenewalQuestionModal: false,
            showAutoRenewalQuestionSuccessModal: false,
            reuseCard: false,
            doRedirectToAllianzPromotionPage: false,
            doRedirectToAllianzOnlinePage: false,
            marketingPreferencesEdited: false,
            marketingPreferenceCheckboxStates: {},
            marketingPreferencesSaving: false,
            showMarketingPreferencesInSuccessPopup: false,
            newQuotePopUpForProduct: "",
            hasFailedCardRefundAndApplicableForDD: false,
            cardRefundStatusChecked: false
        } as T;

        this.multiPolicyAccount = window.sessionStorage.getItem("multiPolicyAccount") === "true"; // NOSONAR
        this.hideAutoRenewalBanner = window.sessionStorage.getItem("hideAutoRenewalBanner") === "true"; // NOSONAR

        this.turnOnNextYearRenewal = this.turnOnNextYearRenewal.bind(this);
        this.setAutomaticRenewal = this.setAutomaticRenewal.bind(this);
        this.setPerformRenewal = this.setPerformRenewal.bind(this);
        this.showNewCarQuotePopUp = this.showNewCarQuotePopUp.bind(this);
        this.showNewHomeQuotePopUp = this.showNewHomeQuotePopUp.bind(this);
        this.product = getProductByPolicyNumber(this.props.params.policyNumber)
    }

    protected readonly marketingPreferenceCheckboxes= [ // NOSONAR
        {type: PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL, label: 'Email', className: 'email', text: 'email'},
        {type: PartyPropertyEnum.MARKETING_PREFERENCES_SMS, label: 'SMS', className: 'sms', text: 'sms'},
        {
            type: PartyPropertyEnum.MARKETING_PREFERENCES_PHONE,
            label: 'Phone',
            className: 'phone',
            icon: 'smartphone',
            text: 'phone'
        },
        {type: PartyPropertyEnum.MARKETING_PREFERENCES_POST, label: 'Post', className: 'post', text: 'post'},
    ]

    private async fetchPartyPropertyByPropertyKey(key: PartyPropertyEnum): Promise<PartyProperty> {
        const observable = this.customerPortalService.getPartyPropertyByPropertyKey(key);
        return await observable.toPromise();
    }

    private async isAllianzPromotion(): Promise<boolean> {
        const observable = this.customerPortalService.isAllianzPromotion();
        return await observable.toPromise();
    }

    protected async setRedirectToAllianzPromotionPage() {
        const isAllianzPromotionShown = getSessionStorage(SessionStorageItemEnum.SESSION_STORAGE_ALLIANZ_PROMOTION_SHOWN);
        if (isAllianzPromotionShown) {
            return;
        }

        const showAllianzPromotionToCustomer = await this.isAllianzPromotion();
        if (!showAllianzPromotionToCustomer) {
            return;
        }

        const showPopup = getSessionStorage("showPopup");
        if (showPopup) {
            return;
        }

        const latestPolicies = await this.customerPortalService.getUserPoliciesWithLatestVersions().toPromise();
        if (!latestPolicies?.length) {
            return;
        }

        this.setState({ doRedirectToAllianzPromotionPage: true });
        setSessionStorage(SessionStorageItemEnum.SESSION_STORAGE_ALLIANZ_PROMOTION_SHOWN, true);

        const partyProperty = await this.fetchPartyPropertyByPropertyKey(PartyPropertyEnum.ALLIANZ_PROMOTION_SHOWN);
        const count = partyProperty?.value ? Number(partyProperty.value) : 0;
        this.customerPortalService.updatePartyProperty(PartyPropertyEnum.ALLIANZ_PROMOTION_SHOWN, (count + 1).toString())
            .subscribe(() => {
                // No-op
            })
    }

    protected async setRedirectToAllianzOnlinePage() {
        const partyProperty = await this.fetchPartyPropertyByPropertyKey(PartyPropertyEnum.ALLIANZ_ONLINE_SHOWN);
        this.customerPortalService.getPolicies().subscribe(policies => {
            const hasPolicies = hasPoliciesBeforeRebranding(policies);
            if ((partyProperty === undefined || partyProperty === null) && hasPolicies) {
                this.setState({ doRedirectToAllianzOnlinePage: true });
                this.customerPortalService.updatePartyProperty(PartyPropertyEnum.ALLIANZ_ONLINE_SHOWN, "false").subscribe(() => {})
            }
        });
    }

    protected showNewCarQuotePopUp() {
        this.setState({newQuotePopUpForProduct: ProductsEnum.WOLF_MOTOR});
    }

    protected showNewHomeQuotePopUp() {
        this.setState({newQuotePopUpForProduct: ProductsEnum.HOME});
    }

    protected setAutomaticRenewal(value: boolean): void {
        this.setState({
            displayOverlay: true
        });

        this.customerPortalService.storePolicyAutomaticRenewalFlag(this.props.params.policyNumber, value)
            .subscribe(() => {
                    this.cacheManager.clearCache(`getPolicyPropertyByPropertyKey${this.props.params.policyNumber},automaticRenewal`);

                    this.setState({
                        automaticRenewal: value,
                        displayOverlay: false
                    })
                }
            )
    }

    protected setPerformRenewal(value: boolean): void {
        this.setState({
            displayOverlay: true
        });

        this.customerPortalService.storePolicyPerformRenewalFlag(this.props.params.policyNumber, value)
            .subscribe(() => {
                    this.cacheManager.clearCache(`getPolicyPropertyByPropertyKey${this.props.params.policyNumber},performRenewal`);

                    this.setState({
                        performRenewal: value,
                        displayOverlay: false
                    })
                }
            )
    };

    protected turnOnNextYearRenewal(): void {
        this.setPerformRenewal(true);
        this.setAutomaticRenewal(true);
        this.setState({
            showRenewalSwitchConfirmation: true
        })
    }

    protected displayReviewDialog() { // NOSONAR
        const createRevPromises: Array<Promise<any>> = [];
        createRevPromises.push(this.customerPortalService.createReviewRequest(this.props.params.policyNumber).toPromise());

        Promise.all(createRevPromises).then(() => {
            // @ts-ignore display feedback dialog after delay
            window[FEEDBACK_DIALOG_DISPLAY_COUNTER] = setTimeout(() => {
                dispatchEvent(new Event(FEEDBACK_DIALOG_DISPLAY_EVENT));
            }, 30_000); // display after 30 seconds
        });
    }

    protected readonly doRedirectToAllianzOnlinePage = (): boolean => { // NOSONAR
        if (this.state.doRedirectToAllianzOnlinePage) {
            this.setState({doRedirectToAllianzOnlinePage: false});
            return true;
        }
        return false;
    }

    protected doRedirectToAllianzPromotionPage = (): boolean => { // NOSONAR
        if (this.state.doRedirectToAllianzPromotionPage) {
            this.setState({doRedirectToAllianzPromotionPage: false});
            return true;
        }

        return false;
    }

    protected showAutoRenewalQuestionModal = (): void => { // NOSONAR
        this.setState({
            showAutoRenewalQuestionModal: true
        });
    }

    // NOSONAR
    protected closeAutoRenewalQuestionSuccessModal = (): void => { // NOSONAR
        this.setState({
            showAutoRenewalQuestionSuccessModal: false
        })
    }

    protected redirectIfDifferentPolicy() {
        if (productHelper.isHomeByProduct(this.product) && POLICY_NUMBER_PREFIX_MAP.WOLF_MOTOR.some(prefix => this.props.params.policyNumber.startsWith(prefix))) {
            this.props.navigate(
                `/dashboard/annualMotor/${this.props.params.policyNumber}`
            );
        }
        if (productHelper.isWolfMotorByProduct(this.product) && POLICY_NUMBER_PREFIX_MAP.HOME.some(prefix => this.props.params.policyNumber.startsWith(prefix))) {
            this.props.navigate(
                `/dashboard/annualHome/${this.props.params.policyNumber}`
            );
        }
    }

    protected setRedirections() { // NOSONAR
        this.redirectIfDifferentPolicy();
        this.setRedirectToAllianzPromotionPage();
        this.setRedirectToAllianzOnlinePage();
    }

    protected getInstalmentsInfoForBanner(showNcdImpactedClaimBanner: boolean) { // NOSONAR
        if (showNcdImpactedClaimBanner) {
            return this.state.allInstalmentsInfo;
        }
        return this.state.missedPaymentInfo ?? this.state.instalmentsInfo
    }

    protected isLackOfFundsMtc(process: string, annualPolicyInfo: AnnualPolicyInfo) { // NOSONAR
        return isCancellationProcess(process) && annualPolicyInfo?.delinquencyType === 'LACK_OF_FUNDS' && +annualPolicyInfo.overdueMonthlyInvoiceAmount > 0;
    }

    protected showNcdImpactedClaimBanner(isMonthly: boolean, interaction: any) { // NOSONAR
        return isMonthly
            && interaction.uistate.policyHasNcdImpactedClaims
            && (interaction.process === INTERACTION_PROCESS_MTC || interaction.process === INTERACTION_PROCESS_SUPPORT_PORTAL_MTC)
            && this.state.allInstalmentsInfo?.remainingAmount !== 0;
    }

    protected closeSuccessPopup = (): void => { // NOSONAR
        if (this.state.showMarketingPreferencesInSuccessPopup && this.state.marketingPreferencesEdited) {
            this.setState({
                displayOverlay: true,
                marketingPreferencesSaving: true
            });
            const marketingPreference: MarketingPreference = this.getMarketingPreference();
            this.customerPortalService.updateMarketingPreferences(marketingPreference).subscribe(
                () => {
                    this.setState({
                        displayOverlay: false,
                        marketingPreferencesSaving: false,
                        marketingPreferencesEdited: false
                    }, this.continueAfterSuccessPopupClose);
                },
                () => {
                    this.setState({
                        displayOverlay: false,
                        marketingPreferencesSaving: false
                    }, this.continueAfterSuccessPopupClose);
                }
            );
            return;
        }
        this.continueAfterSuccessPopupClose();
    };

    private getMarketingPreference(): MarketingPreference {
        const states = this.state.marketingPreferenceCheckboxStates;
        return {
            emailEnabled: states[PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL],
            smsEnabled: states[PartyPropertyEnum.MARKETING_PREFERENCES_SMS],
            postEnabled: states[PartyPropertyEnum.MARKETING_PREFERENCES_POST],
            phoneEnabled: states[PartyPropertyEnum.MARKETING_PREFERENCES_PHONE]
        };
    }

    protected continueAfterSuccessPopupClose(): void {
        // do nothing, child components overrides
    }

    protected fetchQuotes(): void { // NOSONAR
        this.customerPortalService.getQuotes().subscribe(
            (quotes: Quote[]) => {
                this.setState({
                    quotes,
                    quotesLoaded: true
                })
            }
        );
    }

    protected loadHasFailedCardRefundAndApplicableForDDValue(policyNumber: string): void { // NOSONAR
        this.customerPortalService.hasFailedCardRefundAndApplicableForDD(policyNumber)
            .pipe(finalize(() => this.setState({cardRefundStatusChecked: true})))
            .subscribe((hasFailedRefund: boolean) => {this.setState({hasFailedCardRefundAndApplicableForDD: hasFailedRefund});});
    }

    protected fetchInstalmentInfos = (filteredPolicy: Policy) => { // NOSONAR
        const agreementVersionIds: number[] = filteredPolicy.policyHistoryList
            .filter(ph => ph.status !== POLICY_STATUS_MTC)
            .map(ph => ph.id);

        this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds).subscribe((instalmentsInfo: any) => {
            this.setState({instalmentsInfo, instalmentInfoLoaded: true});
        });

        if (filteredPolicy.interaction?.factors?.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL &&
            filteredPolicy.interaction?.renewalDetails?.policyAgeYears > 0) {
            this.customerPortalService.getMissedPaymentInfo(filteredPolicy.interaction.agreementVersionId)
                .subscribe(
                    (missedPaymentInfo: InstalmentsInfo) => {
                        this.setState({missedPaymentInfo});
                    });
        }

        this.customerPortalService.getCombinedInstalmentsInfoByPolicyNumber(this.props.params.policyNumber).subscribe((allInstalmentsInfo: any) => {
            this.setState({allInstalmentsInfo});
        });
    }

    protected loadPolicyHistoryAfterRenewal() { // NOSONAR
        this.customerPortalService.getPolicyHistoryAfterRenewal().subscribe((policyHistories: Policy[]) => {
            this.setState({
                hasOldPolicies: Boolean(policyHistories && policyHistories.length > 0)
            });
        });
    }

    protected loadPartyAndMarketingPreferences(): void { // NOSONAR
        this.customerPortalService.getProfile().subscribe((party: Party) => {
            const marketingPreferenceCheckboxStates: Record<string, boolean> = {
                [PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL),
                [PartyPropertyEnum.MARKETING_PREFERENCES_SMS]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_SMS),
                [PartyPropertyEnum.MARKETING_PREFERENCES_PHONE]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_PHONE),
                [PartyPropertyEnum.MARKETING_PREFERENCES_POST]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_POST)
            };

            const hasAnyMarketingOptIn = Boolean(
                marketingPreferenceCheckboxStates[PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL] ||
                marketingPreferenceCheckboxStates[PartyPropertyEnum.MARKETING_PREFERENCES_SMS] ||
                marketingPreferenceCheckboxStates[PartyPropertyEnum.MARKETING_PREFERENCES_PHONE] ||
                marketingPreferenceCheckboxStates[PartyPropertyEnum.MARKETING_PREFERENCES_POST]
            );

            this.setState({
                party,
                partyLoaded: true,
                marketingPreferenceCheckboxStates,
                showMarketingPreferencesInSuccessPopup: this.referrerJourney === INTERACTION_PROCESS_NB && !hasAnyMarketingOptIn

            });
        });
    }

    private readonly marketingPreferenceDefault = (party: Party, preferenceId: string): boolean => {
        const customProperties = party?.customProperties;
        if (!customProperties) {
            return false;
        }

        return Object.values(customProperties).some(property => property.key === preferenceId && property.value === "true");
    }

    protected filterByProduct(policies: Policy[], product: ProductsEnum): Policy[] { // NOSONAR
        return policies.filter(p => p.products.includes(product));
    }
}
